import { useCallback, useRef, useState } from "react"
import type { SortingState } from "@tanstack/react-table"

import { readStoredOpportunityRowOrder } from "../lib/opportunity-row-order"

/** Matches the page default: highest Findings gap first. */
export const DEFAULT_OPPORTUNITY_COLUMN_SORT: SortingState = [
  { id: "findings", desc: true },
]

function columnSortForSet(tenantId: string | undefined, setId: string | undefined): SortingState {
  if (!tenantId || !setId) return DEFAULT_OPPORTUNITY_COLUMN_SORT
  return readStoredOpportunityRowOrder(tenantId, setId).length > 0
    ? []
    : DEFAULT_OPPORTUNITY_COLUMN_SORT
}

export function listSortParam(
  listSort: SortingState,
): { id: string; desc: boolean } | null {
  const first = listSort[0]
  if (!first) return null
  return { id: first.id, desc: first.desc }
}

/**
 * Header UI sort and the list query sort are separate so a row drag can hide
 * the sort arrows without refetching.
 */
export function useOpportunityListSort(tenantId?: string, setId?: string) {
  const setKey = `${tenantId ?? ""}:${setId ?? ""}`
  const prevSetKeyRef = useRef(setKey)

  const [columnSort, setColumnSort] = useState<SortingState>(() =>
    columnSortForSet(tenantId, setId),
  )
  const [listSort, setListSort] = useState<SortingState>(DEFAULT_OPPORTUNITY_COLUMN_SORT)

  if (prevSetKeyRef.current !== setKey) {
    prevSetKeyRef.current = setKey
    setColumnSort(columnSortForSet(tenantId, setId))
    setListSort(DEFAULT_OPPORTUNITY_COLUMN_SORT)
  }

  const handleColumnSortChange = useCallback((next: SortingState) => {
    setColumnSort(next)
    setListSort(next)
  }, [])

  const handleManualRowReorder = useCallback(() => {
    setColumnSort([])
  }, [])

  return {
    columnSort,
    listSort,
    sortParam: listSortParam(listSort),
    handleColumnSortChange,
    handleManualRowReorder,
  }
}
